// The scenario's state (docs/design/phase1-ui-handoff.md, implementation rules 1–5). Pure. One
// alert lifecycle: an alert exists only while the active stage carries one; acknowledging it
// changes nothing else. Reset returns everything to a clean Stage 1. The simulation interval is
// demonstration speed only; clinical record times come from the stage objects.

import { STAGES, type Stage } from './stages.ts';

export type RunStatus = 'ready' | 'running' | 'paused' | 'completed';
export type AlertState = 'none' | 'active' | 'acknowledged';

export interface AuditEntry {
  action: 'review-acknowledged';
  stage: number;
  /** Demonstration (wall) time, ISO 8601: not clinical record time. */
  at: string;
}

export interface ScenarioState {
  index: number;
  status: RunStatus;
  alert: AlertState;
  /** Transient audit of this simulation run; cleared by Reset. */
  audit: AuditEntry[];
  /** Transient view state (e.g. the selected evidence item). */
  selected?: string;
  /** The Clinician View pop-up is open; Reset closes it (implementation rule 4). */
  clinicianView?: true;
  /**
   * The alert is also shown as a dialog. A view of the same alert, not a second alert system
   * (implementation rule 3): it opens when a stage raises the alert, and Close, Acknowledge or
   * Reset closes it. Closing it leaves the alert active.
   */
  alertDialog?: true;
}

export type ScenarioAction =
  | { type: 'start' }
  | { type: 'pause' }
  | { type: 'next' }
  | { type: 'tick' }
  | { type: 'acknowledge'; at: string }
  | { type: 'select'; item: string | undefined }
  | { type: 'clinician-view'; open: boolean }
  | { type: 'close-alert-dialog' }
  | { type: 'reset' };

export const INITIAL: ScenarioState = { index: 0, status: 'ready', alert: 'none', audit: [] };

export const stageOf = (state: ScenarioState): Stage => STAGES[state.index] ?? (STAGES[0] as Stage);
const LAST = STAGES.length - 1;

function enter(state: ScenarioState, index: number, status: RunStatus): ScenarioState {
  const stage = STAGES[index];
  // A new stage with an alert raises it; a stage without one has none.
  const alert: AlertState =
    stage?.alert === undefined ? 'none' : index === state.index ? state.alert : 'active';
  const next: ScenarioState = {
    ...state,
    index,
    status: index >= LAST ? 'completed' : status,
    alert,
  };
  if (alert === 'active' && state.alert !== 'active') next.alertDialog = true;
  if (alert === 'none') delete next.alertDialog;
  return next;
}

export function reduce(state: ScenarioState, action: ScenarioAction): ScenarioState {
  switch (action.type) {
    case 'start':
      if (state.status === 'completed') return state;
      return { ...state, status: 'running' };
    case 'pause':
      return state.status === 'running' ? { ...state, status: 'paused' } : state;
    case 'next':
      // Starts or advances; never enabled-and-silent (acceptance test 4).
      if (state.index >= LAST) return state;
      return enter(state, state.index + 1, state.status === 'running' ? 'running' : 'paused');
    case 'tick':
      // The interval only advances the predefined sequence.
      if (state.status !== 'running' || state.index >= LAST) return state;
      return enter(state, state.index + 1, 'running');
    case 'acknowledge':
      if (state.alert !== 'active') return state;
      return {
        ...withoutDialog(state),
        alert: 'acknowledged',
        audit: [
          ...state.audit,
          { action: 'review-acknowledged', stage: stageOf(state).number, at: action.at },
        ],
      };
    case 'select': {
      const next: ScenarioState = { ...state };
      if (action.item === undefined) delete next.selected;
      else next.selected = action.item;
      return next;
    }
    case 'clinician-view': {
      const next: ScenarioState = { ...state };
      if (action.open) next.clinicianView = true;
      else delete next.clinicianView;
      return next;
    }
    case 'close-alert-dialog':
      return withoutDialog(state);
    case 'reset':
      return INITIAL;
  }
}

function withoutDialog(state: ScenarioState): ScenarioState {
  const next: ScenarioState = { ...state };
  delete next.alertDialog;
  return next;
}

export const STATUS_COPY: Readonly<Record<RunStatus, string>> = {
  ready: 'Ready',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
};
